import { create } from 'zustand';
import { ILORPContactDetails } from '@src/components/lorp/lorp-contact-details/LORPContactDetailsStep';
import { getMaxBoxesForShipment } from '@src/utils';
import { IReturnSession } from '../hooks/useReturnSession';
import {
  IReturnsAlert,
  ItemToReturn,
  MilitaryExchange,
} from '../models/returns';
import {
  FraudDecision,
  IPolicyValidatorResponse,
  IReturnEligibilityRequest,
  IReturnEligibilityResponse,
  PolicyValidatorShipment,
} from '../models/api';
import { GetReturnsEligibilityDetails } from '../hooks/returns-hooks';
import { RETURN_METHOD, ReturnMethodType } from '../constants/general';

// Shipment object that includes additional states for each shipment such as box quantity and lorp checkbox state
export interface IReturnShipments extends PolicyValidatorShipment {
  shipmentId: number;
  // UPS only
  boxQuantity?: number;
  maxBoxes?: number;
  // Populated after Create Return API call - each shipment gets the returnByDate from its returnCreate API response
  returnByDate?: string;
  // LORP only
  hasAgreed?: boolean; // used to track acknowledgement checkbox
  lorpContactDetails?: ILORPContactDetails;
  // SORIS only - populated after SORIS Create API call
  sorisId?: string;
  sorisQRCode?: string;
}
interface IReturnContext extends IReturnSession {
  returnEligibilityDetails?: IReturnEligibilityResponse;
  returnEligibilityLoading: boolean;
  policyValidatorResponse?: IPolicyValidatorResponse['returnService']['ssrHRTVResponse'];
  alertState: IReturnsAlert | null;
  selectedReturnItems: ItemToReturn[];
  returnMethod: ReturnMethodType;
  orderBarcode?: string;
  receiptNumber?: string;
  lorpContactDetails?: ILORPContactDetails;
  isMultiAddressReturn?: boolean;
  returnShipments?: IReturnShipments[];
  returnBoxes: number;
  multipleReturnBoxes: number[];
  shipmentGroupedLineItems?: ItemToReturn[][];
  selectedItems: ItemToReturn[];
  fraudDecisions: FraudDecision[];
}

const initialState = {
  orderDetailsV2: undefined,
  orderId: undefined,
  isMilitaryExchangeGuest: false,
  tenders: [],
  returnEligibilityDetails: undefined,
  returnEligibilityLoading: false,
  policyValidatorResponse: undefined,
  alertState: null,
  selectedReturnItems: [],
  returnMethod: RETURN_METHOD.IN_STORE as ReturnMethodType,
  orderBarcode: '',
  receiptNumber: '',
  militaryExchange: '' as MilitaryExchange,
  lorpContactDetails: undefined,
  isMultiAddressReturn: false,
  returnShipments: undefined,
  returnBoxes: 1,
  multipleReturnBoxes: [],
  shipmentGroupedLineItems: undefined,
  orderDetailsPath: '/myaccount/order-details',
  selectedItems: [],
  ptcNickname: undefined,
  isPTCOrder: undefined,
  fraudDecisions: [],
};

export const useReturnStore = create<IReturnContext>()(() => ({
  ...initialState,
}));

export const setReturnSession = ({
  orderDetailsV2,
  tenders,
  orderId,
  isMilitaryExchangeGuest,
  militaryExchange,
  orderBarcode,
  receiptNumber,
  orderDetailsPath = '/myaccount/order-details',
  ptcNickname,
  isPTCOrder,
}: IReturnSession) =>
  useReturnStore.setState({
    orderDetailsV2,
    tenders,
    orderId,
    isMilitaryExchangeGuest,
    militaryExchange,
    orderBarcode,
    receiptNumber,
    orderDetailsPath,
    ptcNickname,
    isPTCOrder,
  });

export const setReturnMethod = (value: ReturnMethodType) =>
  useReturnStore.setState({ returnMethod: value });

export const setReturnEligibilityLoading = (value: boolean) =>
  useReturnStore.setState({ returnEligibilityLoading: value });

// TODO we can remove returnBoxes once multipleReturnBoxes is fully implemented
export const setReturnBoxes = (value: number) =>
  useReturnStore.setState({ returnBoxes: value });

export const setMultipleReturnBoxes = (value: number[]) =>
  useReturnStore.setState({ multipleReturnBoxes: value });

export const setReturnEligibilityDetails = (
  value: IReturnEligibilityResponse,
) => useReturnStore.setState({ returnEligibilityDetails: value });

export const setAlertState = (value: IReturnsAlert | null) =>
  useReturnStore.setState({ alertState: value });

export const setSelectedReturnItems = (value: ItemToReturn[]) =>
  useReturnStore.setState({ selectedReturnItems: value });

export const setSelectedItems = (value: ItemToReturn[]) =>
  useReturnStore.setState({ selectedItems: value });

export const setUpdatedItem = (updatedItem: ItemToReturn) =>
  useReturnStore.setState((state) => ({
    selectedItems: state.selectedItems.map((item) =>
      item.lineItemId === updatedItem.lineItemId ? updatedItem : item,
    ),
  }));

export const setUpdatedShipmentFields = (
  shipmentIds: number[],
  fields: Partial<IReturnShipments>,
) => {
  useReturnStore.setState((state) => ({
    returnShipments: state.returnShipments?.map((shipment) =>
      shipmentIds.includes(shipment.shipmentId)
        ? { ...shipment, ...fields }
        : shipment,
    ),
  }));
};

export const setIsMultiAddressReturn = (value: boolean) =>
  useReturnStore.setState({ isMultiAddressReturn: value });

export const setReturnShipments = (value: IReturnShipments[]) =>
  useReturnStore.setState({ returnShipments: value });

export const deleteItemFromShipment = (itemIdToRemove: string) => {
  useReturnStore.setState((state) => {
    const newShipments = state.returnShipments?.map((shipment) => {
      if (
        !shipment.lineItems.some((item) => item.lineItemId === itemIdToRemove)
      ) {
        return shipment;
      }
      const updatedLineItems = shipment.lineItems.filter(
        (item) => item.lineItemId !== itemIdToRemove,
      );
      const modifiedShipment = { ...shipment, lineItems: updatedLineItems };

      return modifiedShipment;
    });
    const filteredShipments = newShipments?.filter(
      (shipment) => shipment.lineItems.length > 0,
    );
    return {
      returnShipments: filteredShipments,
    };
  });
};

export const updateMaxBoxesShipments = (modifiedItems: ItemToReturn[]) => {
  useReturnStore.setState((state) => ({
    returnShipments: state.returnShipments?.map((shipment) => {
      if (shipment.shipmentType !== 'DROPOFF') {
        return shipment;
      }
      const maxBoxes = getMaxBoxesForShipment(
        shipment,
        modifiedItems,
        state.orderDetailsV2,
      );

      return {
        ...shipment,
        maxBoxes,
      };
    }),
  }));
};

export const setShipmentGroupedLineItems = (value: ItemToReturn[][]) =>
  useReturnStore.setState({ shipmentGroupedLineItems: value });

export const getReturnEligibilityDetails = (
  requestData: IReturnEligibilityRequest,
) => {
  setReturnEligibilityLoading(true);
  return GetReturnsEligibilityDetails(
    requestData,
    setReturnEligibilityDetails,
    setAlertState,
  ).finally(() => setReturnEligibilityLoading(false));
};

export const setPolicyValidatorResponse = (
  response: IPolicyValidatorResponse['returnService']['ssrHRTVResponse'],
) => useReturnStore.setState({ policyValidatorResponse: response });

export const setLorpContactDetails = (value: ILORPContactDetails) =>
  useReturnStore.setState({ lorpContactDetails: value });

export const _mockReturnStore = (mockedState: Partial<IReturnContext>) =>
  useReturnStore.setState((state) => ({ ...state, ...mockedState }));

export const _resetReturnStore = () =>
  useReturnStore.setState(initialState, true);

export const setFraudDecisions = (fraudDecisions: FraudDecision[]) => {
  useReturnStore.setState((state) => ({ ...state, fraudDecisions }));
};
